import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronsUpDown, Pencil } from 'lucide-react'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import applicationStyles from '@/features/application/styles/applicationList.module.css'
import { PageShell } from '@/components/layout/PageShell'
import { ModuleScreenLoading } from '@/components/ui/ModuleScreenLoading'
import { useIsMobile } from '@/hooks/useIsMobile'
import { cn } from '@/lib/utils/helpers'
import { NotificationEditModal } from '../components/NotificationEditModal'
import {
  getNotificationActions,
  updateNotificationAction,
} from '../services/notificationSettingsService'
import type {
  NotificationAction,
  NotificationListParams,
  NotificationSortKey,
  NotificationSubmitPayload,
  SortDirection,
} from '../types'
import { LIBERTY_ROLES } from '../types'

const DEFAULT_PARAMS: NotificationListParams = {
  orderBy: 'notification_type',
  sortBy: 'asc',
}

/** Split API `role_added` when display names are concatenated without separators. */
function formatRoleAdded(roleAdded: string): string {
  const trimmed = roleAdded.trim()
  if (!trimmed) return '—'
  if (/,/.test(trimmed)) {
    return trimmed
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean)
      .join(', ')
  }

  const labels = Object.values(LIBERTY_ROLES).slice().sort((a, b) => b.length - a.length)
  const found: string[] = []
  let remaining = trimmed

  while (remaining.length > 0) {
    const match = labels.find((label) => remaining.startsWith(label))
    if (!match) {
      if (remaining) found.push(remaining)
      break
    }
    found.push(match)
    remaining = remaining.slice(match.length)
  }

  return found.length ? found.join(', ') : trimmed
}

function renderRoles(action: NotificationAction): string {
  if (action.roles && action.roles.length) {
    return action.roles
      .map((key) => LIBERTY_ROLES[key as keyof typeof LIBERTY_ROLES] ?? key)
      .join(', ')
  }
  if (action.role_added) return formatRoleAdded(action.role_added)
  return '—'
}

export function NotificationSettingsListPage() {
  const [actions, setActions] = useState<NotificationAction[]>([])
  const [params, setParams] = useState<NotificationListParams>(DEFAULT_PARAMS)
  const [loading, setLoading] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<NotificationAction | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const isMobile = useIsMobile()
  const isListView = !isMobile
  const mountedRef = useRef(false)

  const load = useCallback(async (next: NotificationListParams) => {
    setLoading(true)
    try {
      setActions(await getNotificationActions(next))
    } catch (error) {
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (mountedRef.current) return
    mountedRef.current = true
    void load(DEFAULT_PARAMS)
  }, [load])

  const applyParams = useCallback(
    (next: NotificationListParams) => {
      setParams(next)
      void load(next)
    },
    [load],
  )

  const toggleSort = (key: NotificationSortKey) => {
    const nextDir: SortDirection =
      params.orderBy === key && params.sortBy === 'asc' ? 'desc' : 'asc'
    applyParams({ ...params, orderBy: key, sortBy: nextDir })
  }

  const openEdit = (action: NotificationAction) => {
    setEditTarget(action)
    setEditOpen(true)
  }

  const handleSubmit = async (anId: number, payload: NotificationSubmitPayload) => {
    setSubmitting(true)
    try {
      await updateNotificationAction(anId, payload)
      showSuccess('Data Updated Successfully')
      setEditOpen(false)
      setEditTarget(null)
      void load(params)
    } catch (error) {
      showApiError(error)
    } finally {
      setSubmitting(false)
    }
  }

  const SortIndicator = ({ column }: { column: NotificationSortKey }) => {
    const isActive = params.orderBy === column
    return (
      <span
        className={cn(applicationStyles.sortIcon, isActive && applicationStyles.sortIconActive)}
        aria-hidden
      >
        {isActive ? (params.sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
      </span>
    )
  }

  return (
    <PageShell
      title="NOTIFICATION SETTINGS"
      pageHeaderTestIdRoot="notification-settings-page-header"
    >
      <div
        className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
        data-testid="notification-settings-results-card"
      >
        <div
          className="flex flex-col items-stretch justify-between gap-3 border-b border-slate-100 bg-slate-50/50 px-3 py-3 sm:flex-row sm:items-center sm:px-6 sm:py-4"
          data-testid="notification-settings-summary-bar"
        >
          <div className="text-center text-xs font-medium text-slate-500 sm:text-left sm:text-sm">
            Total{' '}
            <span className="font-bold text-slate-900">{loading ? '…' : actions.length}</span>{' '}
            Records
          </div>
        </div>

        {loading ? (
          <ModuleScreenLoading data-testid="notification-settings-loading" />
        ) : isListView ? (
          <div className="overflow-x-auto" data-testid="notification-settings-table-wrap">
            <table
              className={applicationStyles.listingTable}
              data-testid="notification-settings-table"
            >
              <thead>
                <tr>
                  <th
                    aria-sort={
                      params.orderBy === 'notification_type'
                        ? params.sortBy === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    <button
                      type="button"
                      className={applicationStyles.sortHeaderButton}
                      onClick={() => toggleSort('notification_type')}
                    >
                      <span className={applicationStyles.sortHeaderLabel}>Notification Type</span>
                      <SortIndicator column="notification_type" />
                    </button>
                  </th>
                  <th>Roles</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {actions.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                      No notification settings configured.
                    </td>
                  </tr>
                ) : (
                  actions.map((action) => (
                    <tr key={action.an_id}>
                      <td className="font-medium text-slate-900">
                        {action.notification_type}
                      </td>
                      <td>{renderRoles(action)}</td>
                      <td>
                        <button
                          type="button"
                          onClick={() => openEdit(action)}
                          className="rounded-lg p-1.5 text-[#1597ff] hover:bg-[#1597ff]/10"
                          aria-label="Edit"
                          title="Edit"
                          data-testid={`notification-settings-edit-${action.an_id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div
            className="grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 sm:p-4"
            data-testid="notification-settings-grid"
          >
            {actions.length === 0 ? (
              <p className="col-span-full py-8 text-center text-sm text-slate-500">
                No notification settings configured.
              </p>
            ) : (
              actions.map((action) => (
                <div
                  key={action.an_id}
                  className="space-y-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
                >
                  <div>
                    <p className="text-xs font-semibold text-black">Notification Type</p>
                    <p className="text-sm font-medium text-slate-900">
                      {action.notification_type}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-black">Roles</p>
                    <p className="text-sm text-slate-700">{renderRoles(action)}</p>
                  </div>
                  <div className="flex justify-end border-t border-slate-100 pt-2">
                    <button
                      type="button"
                      onClick={() => openEdit(action)}
                      className="rounded-lg p-1.5 text-[#1597ff] hover:bg-[#1597ff]/10"
                      aria-label="Edit"
                      title="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <NotificationEditModal
        isOpen={editOpen}
        action={editTarget}
        submitting={submitting}
        onClose={() => {
          setEditOpen(false)
          setEditTarget(null)
        }}
        onSubmit={handleSubmit}
      />
    </PageShell>
  )
}
